import { createContext, useState, useContext, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import * as authApi from "@/services/authApi";

export const UserContext = createContext();

const getInitialUser = () => {
  if (typeof window === "undefined") return null;

  const storedUser = localStorage.getItem("user");
  if (storedUser && storedUser !== "undefined") {
    try {
      return JSON.parse(storedUser);
    } catch (error) {
      console.error("Error parsing user data:", error);
      localStorage.removeItem("user");
      return null;
    }
  }
  return null;
};

const getInitialExpiry = () => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem("tokenExpiry") || null;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(getInitialUser);
  const [expiryTime, setExpiryTime] = useState(getInitialExpiry);
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [sessionExpiredReason, setSessionExpiredReason] = useState(null);

  const inactivityTimerRef = useRef(null);
  const isLoggingOutRef = useRef(false);

  const loginUser = (userData, tokenExpiryTimestamp) => {
    setUser(userData);
    if (typeof window !== "undefined") {
      localStorage.setItem("user", JSON.stringify(userData));
      localStorage.setItem("tokenExpiry", tokenExpiryTimestamp);
      setExpiryTime(tokenExpiryTimestamp);
    }
    isLoggingOutRef.current = false;
  };

  const logoutUser = async (reason = "manual") => {
    console.log("Initiating logout process");
    if (isLoggingOutRef.current) {
      return;
    }

    isLoggingOutRef.current = true;

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }

    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout error:", error);
    }

    setUser(null);
    if (typeof window !== "undefined") {
      localStorage.removeItem("user");
      localStorage.removeItem("tokenExpiry");
      localStorage.removeItem("login");
      setExpiryTime(null);
    }

    if (reason === "token_expired") {
      setSessionExpiredReason("token");
      setShowSessionExpiredModal(true);
    }
  };

  const updateUser = (updatedUserData) => {
    setUser(updatedUserData);
    if (typeof window !== "undefined") {
      localStorage.setItem("user", JSON.stringify(updatedUserData));
    }
  };

  const closeModal = () => {
    setShowSessionExpiredModal(false);
    setSessionExpiredReason(null);
  };

  const resetInactivityTimer = () => {
    if (isLoggingOutRef.current || showSessionExpiredModal) {
      return;
    }

    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    const inactivityTimeout =
      parseInt(process.env.NEXT_PUBLIC_INACTIVITY_TIMEOUT) || 30 * 60 * 1000;

    const timer = setTimeout(() => {
      if (!isLoggingOutRef.current && user) {
        setSessionExpiredReason("inactivity");
        setShowSessionExpiredModal(true);
      }
    }, inactivityTimeout);
    inactivityTimerRef.current = timer;
  };

  const handleStayConnected = () => {
    setShowSessionExpiredModal(false);
    setSessionExpiredReason(null);

    resetInactivityTimer();
  };

  useEffect(() => {
    if (!expiryTime) return;

    const remaining = expiryTime - Date.now();

    if (remaining <= 0) {
      logoutUser("token_expired");
      return;
    }

    const timer = setTimeout(() => {
      logoutUser("token_expired");
    }, remaining);

    return () => clearTimeout(timer);
  }, [expiryTime]);

  useEffect(() => {
    if (!user || isLoggingOutRef.current) {
      return;
    }

    resetInactivityTimer();

    const events = ["mousemove", "keydown", "scroll", "click"];
    events.forEach((event) =>
      window.addEventListener(event, resetInactivityTimer)
    );

    return () => {
      events.forEach((event) =>
        window.removeEventListener(event, resetInactivityTimer)
      );
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
    };
  }, [user, showSessionExpiredModal]);

  return (
    <UserContext.Provider
      value={{
        user,
        loginUser,
        logoutUser,
        updateUser,
        showSessionExpiredModal,
        sessionExpiredReason,
        closeModal,
        handleStayConnected,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);